import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import { 
  Shield, 
  ArrowRight, 
  Phone, 
  Mail, 
  Search,
  Users,
  CheckCircle2,
  Heart,
  ChevronRight,
  BadgeCheck,
  Zap,
  Clock,
  RefreshCw,
  TrendingUp,
  FileText,
  AlertCircle,
  IndianRupee,
  Hospital,
  HeartPulse,
  Banknote,
  Pill,
  ShieldCheck,
  Ambulance,
  XCircle,
  Scale,
  BarChart3,
  Layers,
  FileSearch,
  Star,
  Quote,
  ChevronDown,
  ExternalLink,
  LifeBuoy,
  ShieldAlert
} from "lucide-react";

const API_URL = process.env.REACT_APP_BACKEND_URL;

const INSURER_LOGOS = [
  { name: "HDFC ERGO", url: "https://upload.wikimedia.org/wikipedia/commons/a/aa/HDFC_ERGO_General_Insurance_Company.svg" },
  { name: "Care Health", url: "https://upload.wikimedia.org/wikipedia/commons/9/90/Care_health_insurance_logo.png" },
  { name: "ICICI Lombard", url: "https://upload.wikimedia.org/wikipedia/en/thumb/0/05/ICICI_Lombard.svg/250px-ICICI_Lombard.svg.png" },
  { name: "Aditya Birla", url: "https://upload.wikimedia.org/wikipedia/en/7/75/Aditya_Birla_Group_Logo.svg" },
  { name: "Niva Bupa", url: "https://upload.wikimedia.org/wikipedia/commons/f/fb/Niva_Bupa_Logo.svg" },
  { name: "Star Health", url: "https://upload.wikimedia.org/wikipedia/en/7/7e/Star_Health_Insurance_logo.png" },
  { name: "HDFC Life", url: "https://upload.wikimedia.org/wikipedia/commons/b/b5/HDFC-Life-Logo.jpg" },
  { name: "Tata AIA", url: "https://upload.wikimedia.org/wikipedia/commons/4/4f/TataAIA.png" },
  { name: "ICICI Prudential", url: "https://upload.wikimedia.org/wikipedia/commons/9/91/ICICI-Prudential-Mutual-Fund.jpg" }
];

const LANDING_FAQS = [
  {
    q: "I have a pre-existing condition like diabetes or BP. Can I still get good health insurance?",
    a: "Yes — this is our specialisation. Conditions like diabetes, hypertension or thyroid don't disqualify you; they change which insurer and plan is right for you. Different underwriters treat the same condition very differently, and we recommend accordingly — with waiting periods and loadings explained upfront, before you pay."
  },
  {
    q: "How is buying through Kavach Box different from buying on an online portal?",
    a: "Portals sell policies. We stand by claims. A portal's job ends at checkout; ours begins there — policy reviews, renewal advice and, most importantly, end-to-end claim support from hospitalisation to settlement, handled personally."
  },
  {
    q: "Do you charge for consultations or policy reviews?",
    a: "No. Consultations, policy reviews and second opinions are completely free, with no obligation. We are compensated by insurers the same way any distributor is — but we advise across companies, so we recommend what fits you, not what pays most."
  },
  {
    q: "What happens if my claim gets stuck or rejected?",
    a: "First, we work to prevent it: honest disclosure at proposal and correct documentation at claim time prevent most rejections. If an insurer questions a claim, we handle re-submission and escalation ourselves — directly with the insurer's claims team."
  },
  {
    q: "Should I port my existing policy or buy a fresh one?",
    a: "It depends on the waiting periods you have already served. Porting carries forward that credit; a fresh policy restarts the clock. Upload your policy to our free Policy X-Ray or talk to us — we'll tell you honestly whether porting even makes sense for you."
  },
  {
    q: "Which insurers do you advise across?",
    a: "We advise across leading insurers including HDFC ERGO, Care Health, Star Health, ICICI Lombard, Aditya Birla, Niva Bupa, HDFC Life, Tata AIA and more — and recommend based on your profile, not on any single company."
  }
];

const GoogleGIcon = ({ className = "h-4 w-4" }) => (
  <svg className={className} viewBox="0 0 48 48" aria-hidden="true">
    <path fill="#FFC107" d="M43.6 20.1H42V20H24v8h11.3C33.7 32.7 29.2 36 24 36c-6.6 0-12-5.4-12-12s5.4-12 12-12c3.1 0 5.9 1.2 8 3l5.7-5.7C34.3 6.1 29.4 4 24 4 13 4 4 13 4 24s9 20 20 20 20-9 20-20c0-1.3-.1-2.6-.4-3.9z"/>
    <path fill="#FF3D00" d="M6.3 14.7l6.6 4.8C14.7 15.1 19 12 24 12c3.1 0 5.9 1.2 8 3l5.7-5.7C34.3 6.1 29.4 4 24 4 16.3 4 9.7 8.3 6.3 14.7z"/>
    <path fill="#4CAF50" d="M24 44c5.2 0 9.9-2 13.4-5.2l-6.2-5.2C29.2 35.1 26.7 36 24 36c-5.2 0-9.6-3.3-11.3-8l-6.5 5C9.5 39.6 16.2 44 24 44z"/>
    <path fill="#1976D2" d="M43.6 20.1H42V20H24v8h11.3c-.8 2.2-2.2 4.2-4.1 5.6l6.2 5.2C36.9 40.1 44 35 44 24c0-1.3-.1-2.6-.4-3.9z"/>
  </svg>
);

const StatCounter = ({ end, suffix = "+", label }) => {
  const ref = useRef(null);
  const started = useRef(false);
  const [val, setVal] = useState(0);
  useEffect(() => {
    const obs = new IntersectionObserver(([e]) => {
      if (e.isIntersecting && !started.current) {
        started.current = true;
        const dur = 1600, t0 = performance.now();
        const tick = (t) => {
          const p = Math.min((t - t0) / dur, 1);
          setVal(Math.round(end * (1 - Math.pow(1 - p, 3))));
          if (p < 1) requestAnimationFrame(tick);
        };
        requestAnimationFrame(tick);
      }
    }, { threshold: 0.4 });
    if (ref.current) obs.observe(ref.current);
    return () => obs.disconnect();
  }, [end]);
  return (
    <div ref={ref} className="glass-card p-6 text-center">
      <p className="text-3xl sm:text-4xl font-extrabold text-[#A87608]">{val}{suffix}</p>
      <p className="text-[#2E3B57] text-sm mt-1">{label}</p>
    </div>
  );
};

const ReviewCard = ({ review }) => (
  <div className="glass-card p-6 w-[300px] sm:w-[350px] flex-shrink-0 mx-3 flex flex-col">
    <Quote className="h-6 w-6 text-[#A87608] mb-3 flex-shrink-0" />
    <p className="text-[#2E3B57] text-sm leading-relaxed flex-1">"{review.text}"</p>
    <div className="flex items-center gap-3 mt-5 pt-4 border-t border-[#C9D4E6]">
      <div className="h-10 w-10 rounded-full bg-gradient-to-br from-[#D4A017] to-[#D9A82C] flex items-center justify-center flex-shrink-0">
        <span className="text-[#0D1A3A] font-bold text-sm">
          {(review.reviewer_name || "?").split(" ").map(w => w[0]).slice(0, 2).join("").toUpperCase()}
        </span>
      </div>
      <div className="min-w-0">
        <p className="text-[#1B2A4A] font-semibold text-sm truncate">{review.reviewer_name}</p>
        <div className="flex items-center gap-1.5">
          <div className="flex">
            {[1, 2, 3, 4, 5].map(i => (
              <Star key={i} className={`h-3 w-3 ${i <= (review.rating || 5) ? "text-[#A87608] fill-[#D4A017]" : "text-[#3A4557]"}`} />
            ))}
          </div>
          <span className="text-[#3A4557] text-xs flex items-center gap-1">
            <GoogleGIcon className="h-3 w-3" /> Google review
          </span>
        </div>
      </div>
    </div>
  </div>
);

const API_BASE = process.env.REACT_APP_BACKEND_URL;

const TestimonialCard = ({ item }) => (
  <div className="glass-card w-[300px] sm:w-[340px] flex-shrink-0 mx-3 flex flex-col overflow-hidden">
    {item.has_video ? (
      <video
        src={`${API_BASE}${item.video_url}`}
        controls
        playsInline
        preload="metadata"
        className="w-full h-[220px] bg-black object-cover"
      />
    ) : (
      <div className="p-6 flex-1 flex flex-col">
        <Quote className="h-6 w-6 text-[#A87608] mb-3" />
        <p className="text-[#2E3B57] text-sm leading-relaxed flex-1">"{item.message}"</p>
      </div>
    )}
    <div className="flex items-center gap-3 p-4 border-t border-[#C9D4E6]">
      <div className="h-10 w-10 rounded-full bg-gradient-to-br from-[#D4A017] to-[#D9A82C] flex items-center justify-center flex-shrink-0">
        <span className="text-[#0D1A3A] font-bold text-sm">
          {(item.customer_name || "?").split(" ").map(w => w[0]).slice(0, 2).join("").toUpperCase()}
        </span>
      </div>
      <div className="min-w-0 flex-1">
        <p className="text-[#1B2A4A] font-semibold text-sm truncate">{item.customer_name}{item.customer_city ? `, ${item.customer_city}` : ""}</p>
        <div className="flex">
          {[1, 2, 3, 4, 5].map(i => (
            <Star key={i} className={`h-3 w-3 ${i <= (item.rating || 5) ? "text-[#A87608] fill-[#D4A017]" : "text-[#3A4557]"}`} />
          ))}
        </div>
      </div>
    </div>
  </div>
);


// Health Insurance Checklist - 11 key points with detailed explanations for beginners
const HEALTH_CHECKLIST = [
  {
    icon: Hospital,
    title: "No Room Rent Capping",
    shortDesc: "Choose any room without paying extra",
    fullDesc: "When you're hospitalized, you get a room. Some policies say 'we'll only pay ₹5,000/day for room' or '1% of your sum insured'. If your room costs more, not only do you pay the extra room cost, but your ENTIRE hospital bill (medicines, surgery fees, doctor visits) gets reduced proportionately. This can result in lakhs of rupees from your pocket.",
    example: "Example: Hospital bill is ₹5 Lakhs. Room costs ₹8,000/day but policy covers ₹4,000. You don't just pay ₹4,000 extra per day - your entire ₹5 Lakh bill gets cut by 50%. You end up paying ₹2.5 Lakhs+ from pocket!",
    lookFor: "Any Room Category or 'No Sub-limits on Room Rent' or 'Room Rent at Actuals'",
    avoid: "Plans with room rent limit like '1% of SI' or fixed amount like '₹5,000/day'"
  },
  {
    icon: AlertCircle,
    title: "No Sub-Limits of Any Kind",
    shortDesc: "Your full sum insured should be available for everything",
    fullDesc: "Sub-limits are hidden caps within your policy that limit how much you can claim for specific items - even if you have a large sum insured. Common sub-limits include: Room Rent (₹5,000/day), ICU Charges (₹10,000/day), Surgeon Fees (% of SI), Anesthesia, Ambulance, etc. When any sub-limit is breached, your entire claim gets reduced proportionately - not just that item!",
    example: "Example: You have ₹20 Lakh policy but ICU limit is ₹10,000/day. Actual ICU cost is ₹25,000/day. This 60% breach doesn't just cut ICU payment - it reduces your ENTIRE bill by 60%. A ₹10 Lakh surgery bill? You only get ₹4 Lakhs!",
    lookFor: "No Sub-Limits or 'All expenses covered at actuals up to Sum Insured' - Room, ICU, Surgeon fees, all should be without caps",
    avoid: "Any policy with sub-limits on Room Rent, ICU, Surgeon/Anesthetist fees, Diagnostic tests, or Medicines"
  },
  {
    icon: Pill,
    title: "Consumables / Non-Medical Items Cover",
    shortDesc: "Gloves, PPE kits, syringes - all covered",
    fullDesc: "During hospitalization, hospitals charge for many 'non-medical' or 'consumable' items - things that are used once and thrown away. These include: Gloves, Syringes, PPE Kits, Masks, Cotton, Bandages, Surgical Disposables, Oximeter Probe, Catheter, IV Set, etc. These can add up to ₹20,000-50,000 or more! Many basic policies DON'T cover these, so you pay from pocket.",
    example: "Example: After a 5-day hospitalization, the hospital gives you a bill with ₹35,000 in 'consumables' - gloves, PPE, syringes, IV sets, catheters. Without consumable cover, you pay this entire amount yourself even though you have insurance!",
    lookFor: "Consumables Cover included in base plan OR 'Non-Medical Items Cover' OR 'Claim Protector' rider that covers consumables",
    avoid: "Policies that exclude consumables/non-medical items - check the exclusions list carefully"
  },
  {
    icon: RefreshCw,
    title: "Restore / Recharge Benefit",
    shortDesc: "Your coverage refills after a claim",
    fullDesc: "Imagine your family has ₹10 Lakh coverage. Your father uses ₹8 Lakhs for heart surgery in March. Now only ₹2 Lakhs is left for the rest of the year. What if someone else needs hospitalization? With 'Restore Benefit', after a claim, your full ₹10 Lakhs becomes available again for other family members or different illnesses.",
    example: "Example: ₹10L family policy. Father's surgery uses ₹8L in April. With 100% Restore, mother can still use full ₹10L if she needs hospitalization in August.",
    lookFor: "100% Restore Benefit or Unlimited Restore (coverage refills multiple times in a year)",
    avoid: "No restore benefit, or restore only for 'different illness' or 'different person' (limited use)"
  },
  {
    icon: Banknote,
    title: "No Co-payment",
    shortDesc: "Insurance pays your full bill",
    fullDesc: "Co-payment means YOU pay a percentage of every claim. If your policy has 20% co-pay and hospital bill is ₹5 Lakhs, you pay ₹1 Lakh from your pocket. This happens on EVERY claim, every year. It's like buying insurance but still paying a big chunk yourself. Avoid this, especially in senior citizen policies where co-pay can be 20-30%.",
    example: "Example: You have 10% co-pay. Over 10 years, you make 3 claims of ₹3L, ₹5L, ₹4L. You end up paying ₹1.2 Lakhs from pocket just in co-pay!",
    lookFor: "Zero Co-payment or 'No Mandatory Co-pay' written clearly in policy",
    avoid: "Compulsory co-payment of any percentage, especially 10-20% which is common in cheaper policies"
  },
  {
    icon: Clock,
    title: "Understand the Waiting Periods",
    shortDesc: "Know when your coverage actually starts",
    fullDesc: "After buying insurance, you cannot claim immediately for everything. There are 3 types of waiting periods you MUST understand:\n\n1. INITIAL WAITING (30 days): Nothing is covered except accidents. Buy insurance before you need it!\n\n2. SPECIFIC DISEASE WAITING (2 years): Listed diseases like Hernia, Piles, Fistula, Cataract, Kidney Stones, Sinus, Joint Replacement have their own waiting period.\n\n3. PRE-EXISTING DISEASE WAITING (36-48 months): Conditions you ALREADY have like Diabetes, Blood Pressure, Thyroid, Heart conditions. This is the most important one - if you have any health condition, you must wait 3-4 years before claims related to it are covered.",
    example: "Example: You have Diabetes (PED) and buy a policy with 4-year PED waiting. In Year 2, you're hospitalized for diabetic foot ulcer. Claim REJECTED - you haven't completed 4 years. You pay entire bill from pocket!",
    lookFor: "Shorter PED waiting of 2-3 years instead of 4 years | Plans with 'PED Reduction Rider' that can reduce waiting to 1-2 years | Port your existing policy to carry forward waiting period credit",
    avoid: "4-year PED waiting without any reduction option | Long specific disease waiting periods"
  },
  {
    icon: TrendingUp,
    title: "Good No Claim Bonus (NCB)",
    shortDesc: "Your coverage grows every healthy year",
    fullDesc: "No Claim Bonus is a reward for not making claims. Every year you don't claim, your sum insured INCREASES - typically by 50% per year. So a ₹10 Lakh policy can become ₹15 Lakhs after year 1, ₹20 Lakhs after year 2, and so on. This is FREE extra coverage! But check what happens after you make a claim - does the bonus reset completely or reduce gradually?",
    example: "Example: ₹10L policy with 50% NCB. After 4 healthy years: Year 1 = ₹15L, Year 2 = ₹20L, Year 3 = ₹25L, Year 4 = ₹30L. Triple coverage at same premium!",
    lookFor: "50% NCB per year, accumulating up to 100-200% | NCB that doesn't reset fully after one claim",
    avoid: "Low NCB (10-20% per year) or NCB that vanishes completely after a single claim"
  },
  {
    icon: Ambulance,
    title: "Ambulance Coverage",
    shortDesc: "Emergency transport is covered",
    fullDesc: "In emergencies, ambulance costs can be significant - road ambulance can cost ₹2,000-10,000 depending on distance and type. Air ambulance for remote areas or organ transport can cost ₹5-15 Lakhs! Good policies include road ambulance in the base plan and offer air ambulance either included or as an add-on.",
    example: "Example: Accident on highway, need ambulance to nearest city hospital 50km away. Cost: ₹5,000. Without coverage, you pay from pocket during an already stressful time.",
    lookFor: "Road ambulance covered per hospitalization (₹2,000-5,000) + Air ambulance option available",
    avoid: "Very low ambulance limits (₹500-1,000) or no ambulance coverage at all"
  },
  {
    icon: HeartPulse,
    title: "Day Care Procedures Covered",
    shortDesc: "No 24-hour admission needed for many treatments",
    fullDesc: "Earlier, insurance only paid if you stayed in hospital for 24+ hours. But medical technology has advanced - many procedures like cataract surgery, chemotherapy, dialysis, angiography now take just a few hours. 'Day Care' coverage means these shorter treatments are also covered. Modern policies cover 500+ such procedures.",
    example: "Example: Cataract surgery costs ₹40,000, done in 3 hours. Without day care coverage, claim rejected because you weren't admitted for 24 hours!",
    lookFor: "All Day Care Procedures covered or list of 400+ procedures mentioned",
    avoid: "Limited day care coverage or extra waiting period for day care procedures"
  },
  {
    icon: FileText,
    title: "Pre & Post Hospitalization Cover",
    shortDesc: "Expenses before and after hospital stay are covered",
    fullDesc: "Hospitalization isn't just the days in hospital. Before admission, you have doctor visits, tests, scans. After discharge, you need medicines, follow-up visits, physiotherapy. Pre-hospitalization covers expenses 30-60 days BEFORE admission. Post-hospitalization covers 60-180 days AFTER discharge. Both should be included.",
    example: "Example: Surgery on 15th Jan. Doctor visits & tests from 1st Jan (pre-hosp) and medicines & follow-ups till 15th April (post-hosp) - all covered under one claim.",
    lookFor: "60 days pre-hospitalization + 180 days post-hospitalization coverage",
    avoid: "Very short coverage like only 30 days pre and 60 days post"
  },
  {
    icon: IndianRupee,
    title: "Adequate Sum Insured",
    shortDesc: "Enough coverage for today and tomorrow",
    fullDesc: "Medical costs in India increase by 14-15% every year. A heart surgery costing ₹8 Lakhs today will cost ₹25+ Lakhs in 10 years. Many people buy ₹3-5 Lakh coverage to save on premium, but this is the BIGGEST mistake. For metro cities, minimum ₹10-15 Lakhs is recommended. Remember, with good NCB (No Claim Bonus), your coverage will automatically grow - a ₹10L policy can become ₹20L+ in few years without paying extra!",
    example: "Example: You're 30 years old, buy ₹10L policy with 50% NCB. By age 40, your coverage could be ₹25-30 Lakhs. By age 50 when you need it most, ₹40-50 Lakhs!",
    lookFor: "Minimum ₹10 Lakhs for families in metros | Good NCB to grow coverage | Option to increase sum insured later",
    avoid: "Buying very low coverage (₹3-5L) just to save premium - it's false economy"
  }
];

// Term Insurance Checklist - 8 key points with detailed explanations for beginners
const TERM_CHECKLIST = [
  {
    icon: IndianRupee,
    title: "Adequate Cover Amount",
    shortDesc: "10-15 times your annual income",
    fullDesc: "The primary purpose of term insurance is to replace your income if something happens to you. Your family should be able to maintain their lifestyle, pay off loans, fund children's education, and meet future goals. The thumb rule is: Cover = 10-15 times your annual income + all outstanding loans + future goals (education, marriage).",
    example: "Example: Annual income ₹12 Lakhs + Home loan ₹50 Lakhs + Kids education ₹30 Lakhs = You need at least ₹2 Crore cover (₹1.2Cr income replacement + ₹50L loan + ₹30L education).",
    lookFor: "Calculate actual need, don't just pick a round number | Most people are underinsured",
    avoid: "Buying low cover (₹25-50 Lakhs) just because premium is cheap - it defeats the purpose"
  },
  {
    icon: Clock,
    title: "Cover Till Retirement (60-65 years)",
    shortDesc: "Protection for your entire working life",
    fullDesc: "Your term insurance should cover you until you stop earning and become financially independent (typically 60-65 years). If you buy a policy till only 50 or 55, you leave a gap. After 55, buying new term insurance is very expensive and may have health restrictions.",
    example: "Example: You're 30 years old. Buy cover till 65 = 35 years of protection. If you only buy till 55, you have 10 unprotected years when you might still have loans or dependents.",
    lookFor: "Policy term till age 60-65 | Option to extend if needed",
    avoid: "Short policy terms (10-20 years) unless you have specific short-term needs"
  },
  {
    icon: ShieldCheck,
    title: "High Claim Settlement Ratio",
    shortDesc: "Will the company pay when needed?",
    fullDesc: "Unlike health insurance, you can't 'test' a term policy. The claim happens only once - when it matters most. Check the insurer's Claim Settlement Ratio (CSR) from IRDAI data. A 98% CSR means 98 out of 100 death claims were paid. Also check the number of claims settled - a new company might have high CSR but very few claims.",
    example: "Example: Company A has 99% CSR with 50,000 claims settled. Company B has 98% CSR but only 500 claims. Company A's data is more reliable.",
    lookFor: "CSR above 97% | Large number of claims settled (10,000+) | Established insurer with 10+ years track record",
    avoid: "CSR below 95% | New insurers with very few claims | Don't just go by lowest premium"
  },
  {
    icon: FileText,
    title: "Simple & Clear Policy Terms",
    shortDesc: "No hidden exclusions or clauses",
    fullDesc: "Read the policy document carefully. Check exclusions - most policies exclude suicide in first year, death due to war, adventure sports, etc. Some policies have specific exclusions for pre-existing conditions not disclosed. The simpler and clearer the terms, the less chance of claim rejection.",
    example: "Example: You have mild asthma but didn't disclose it. Years later, you die of heart attack (unrelated to asthma). Company might still reject claim citing 'non-disclosure'. Always disclose everything!",
    lookFor: "Standard exclusions only | Clear terms in policy document | Good insurer reputation for claim settlement",
    avoid: "Policies with too many exclusions | Hiding any health information during application"
  },
  {
    icon: Users,
    title: "Flexible Payout Options",
    shortDesc: "Lump sum, monthly income, or both",
    fullDesc: "When you die, how should your family receive the money? Options include: Lump Sum (full amount at once), Monthly Income (regular payments for 10-20 years), or combination of both. Monthly income option ensures your family doesn't exhaust the entire sum quickly and provides regular income like a salary.",
    example: "Example: ₹1 Crore policy with 'Lump Sum + Monthly Income' - Family gets ₹50 Lakhs immediately + ₹50,000/month for 10 years. This ensures regular income while having emergency funds.",
    lookFor: "Multiple payout options | Income benefit with inflation adjustment | Lump sum + monthly income combo",
    avoid: "Only lump sum option if your family isn't financially savvy - money might get exhausted"
  },
  {
    icon: TrendingUp,
    title: "Option to Increase Cover",
    shortDesc: "Life changes, your cover should too",
    fullDesc: "Your insurance needs change over time - marriage, kids, home loan, salary hikes. A good policy lets you increase cover at life milestones without fresh medical tests. This is crucial because if your health deteriorates later, buying new insurance becomes difficult or expensive.",
    example: "Example: You buy ₹1 Cr at age 28. At 32, you get married and take home loan. With 'Life Stage Benefit', you can increase to ₹1.5 Cr without new medical tests.",
    lookFor: "Life Stage Benefit or Cover Increase Option | Automatic increase option | Without fresh underwriting",
    avoid: "Policies with no option to increase | Policies that require full medical tests for increase"
  },
  {
    icon: Heart,
    title: "Critical Illness & Disability Riders",
    shortDesc: "Protection while you're still alive",
    fullDesc: "Basic term insurance only pays on death. But what if you survive a heart attack or get paralyzed? You can't work, but policy won't pay. Critical Illness rider pays lump sum if you're diagnosed with listed diseases (cancer, heart attack, stroke, etc.). Disability rider pays if you become permanently disabled.",
    example: "Example: You have a massive heart attack at 45. With Critical Illness rider (₹50 Lakhs), you get ₹50 Lakhs immediately for treatment while being alive. Without it, you get nothing till death.",
    lookFor: "Critical Illness rider covering 30+ diseases | Waiver of Premium on disability | Accidental Death Benefit",
    avoid: "Skipping riders to save premium - they're most useful when you survive but can't earn"
  },
  {
    icon: Banknote,
    title: "Waiver of Premium",
    shortDesc: "Policy continues even if you can't pay",
    fullDesc: "What if you become disabled or critically ill and can't pay premiums? Without protection, policy lapses. 'Waiver of Premium' rider means if you're diagnosed with critical illness or become disabled, all future premiums are waived - the insurance company pays them. Your cover continues.",
    example: "Example: At age 40, you're diagnosed with cancer. Treatment costs drain your savings. With Waiver of Premium, you don't need to pay ₹25,000 annual premium anymore - policy continues free till 65.",
    lookFor: "Waiver of Premium on Critical Illness | Waiver on Permanent Disability | Waiver on Accidental Disability",
    avoid: "Not adding this rider - premium is small but benefit is huge when you need it most"
  }
];

const containerVariants = {
  hidden: { opacity: 0 },
  visible: { opacity: 1, transition: { staggerChildren: 0.1 } }
};

const itemVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.5 } }
};

export default function LandingPage() {
  const navigate = useNavigate();
  const [expandedChecklist, setExpandedChecklist] = useState({});
  const [expandedTermChecklist, setExpandedTermChecklist] = useState({});
  const [showHealthModal, setShowHealthModal] = useState(false);
  const [showTermModal, setShowTermModal] = useState(false);
  const [reviewsData, setReviewsData] = useState({ settings: {}, reviews: [] });
  const [testimonials, setTestimonials] = useState([]);
  const [openFaq, setOpenFaq] = useState(null);

  useEffect(() => {
    fetch(`${API_URL}/api/site/reviews`)
      .then(r => r.json())
      .then(d => setReviewsData({ settings: d.settings || {}, reviews: d.reviews || [] }))
      .catch(() => {});
    fetch(`${API_URL}/api/testimonials/public`)
      .then(r => r.json())
      .then(d => setTestimonials(d.testimonials || []))
      .catch(() => {});
  }, []);

  const googleUrl = reviewsData.settings.google_reviews_url;

  const toggleChecklistItem = (idx) => {
    setExpandedChecklist(prev => ({ ...prev, [idx]: !prev[idx] }));
  };

  const toggleTermChecklistItem = (idx) => {
    setExpandedTermChecklist(prev => ({ ...prev, [idx]: !prev[idx] }));
  };

  return (
    <div className="min-h-screen">
      {/* Hero Section - Simplified */}
      <section className="relative pt-6 sm:pt-8 pb-12 px-4 sm:px-6 overflow-hidden">
        {/* Background effects */}
        <div className="absolute bottom-10 left-1/4 w-96 h-96 bg-[#D4A017]/10 rounded-full blur-3xl" />
        <div className="absolute bottom-0 right-1/4 w-96 h-96 bg-[#EDF1F8]/40 rounded-full blur-3xl" />
        <div className="absolute top-1/2 right-10 w-64 h-64 bg-[#D4A017]/5 rounded-full blur-3xl" />

        <div className="max-w-6xl mx-auto relative z-10">
          <div className="grid lg:grid-cols-[1.35fr_1fr] gap-10 lg:gap-14 items-center">
          <motion.div
            variants={containerVariants}
            initial="hidden"
            animate="visible"
            className="text-center lg:text-left"
          >
            <motion.div
              variants={itemVariants}
              onClick={() => navigate("/")}
              className="cursor-pointer -mb-3"
            >
              <img 
                src="/kavachbox-logo-light.png" 
                alt="Kavach Box" 
                className="h-40 sm:h-48 object-contain mx-auto lg:mx-0 lg:-ml-8 -mt-12 sm:-mt-14"
                data-testid="kavachbox-logo"
              />
              <div className="flex items-baseline gap-1.5 -mt-9 sm:-mt-11 mb-7 justify-center lg:justify-start lg:pl-[18px]" data-testid="group-tagline">
                <span className="text-sm sm:text-base text-[#2E3B57] font-medium">A part of</span>
                <img src="/shree-finserv-logo.png" alt="Shree Finserv" className="h-6 sm:h-7 object-contain translate-y-1" />
                <span className="text-sm sm:text-base text-[#2E3B57] font-medium">Group</span>
              </div>
            </motion.div>
            <motion.div variants={itemVariants} className="mb-6 flex flex-wrap items-center justify-center lg:justify-start gap-3">
              <span className="badge-gold">
                <BadgeCheck className="h-4 w-4" />
                Trusted by 1000+ Families
              </span>
              {reviewsData.reviews.length > 0 && (
                <a
                  href={googleUrl || undefined}
                  target={googleUrl ? "_blank" : undefined}
                  rel="noopener noreferrer"
                  onClick={(e) => { if (!googleUrl) e.preventDefault(); }}
                  className={`inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-white shadow-lg shadow-black/20 ${googleUrl ? "hover:scale-105 transition-transform cursor-pointer" : "cursor-default"}`}
                  data-testid="google-rating-pill"
                >
                  <GoogleGIcon className="h-4 w-4" />
                  <span className="text-[#0D1A3A] font-bold text-sm">{reviewsData.settings.google_rating || 5}</span>
                  <div className="flex">
                    {[1, 2, 3, 4, 5].map(i => (
                      <Star key={i} className="h-3.5 w-3.5 text-[#A87608] fill-[#D4A017]" />
                    ))}
                  </div>
                  <span className="text-[#3A4557] text-xs font-medium">
                    {reviewsData.settings.google_review_count ? `${reviewsData.settings.google_review_count}+ Google reviews` : "on Google"}
                    {googleUrl ? " →" : ""}
                  </span>
                </a>
              )}
            </motion.div>
            
            <motion.h1 
              variants={itemVariants}
              className="text-3xl sm:text-4xl lg:text-5xl font-bold leading-tight mb-4"
            >
              <span className="text-[#1B2A4A]">Health & Term Insurance</span>
              <br />
              <span className="text-gradient-gold">Made Simple</span>
            </motion.h1>
            
            <motion.p 
              variants={itemVariants}
              className="text-lg text-[#2E3B57] max-w-2xl mx-auto lg:mx-0 mb-8 leading-relaxed"
            >
              Compare top health and term insurance plans, understand benefits clearly, 
              and find the perfect coverage for your family.
            </motion.p>

            <motion.div variants={itemVariants} className="flex flex-col sm:flex-row gap-3 justify-center lg:justify-start">
              <Button
                onClick={() => navigate("/explore-plans")}
                className="btn-gold h-12 px-6 text-base font-bold"
                data-testid="hero-explore-cta"
              >
                Explore Plans
                <ArrowRight className="h-5 w-5 ml-1.5" />
              </Button>
              <Button
                onClick={() => navigate("/claim-desk")}
                variant="outline"
                className="h-12 px-6 text-base font-semibold border-[#1B2A4A]/25 text-[#1B2A4A] hover:bg-[#1B2A4A] hover:text-white"
                data-testid="hero-claim-desk-cta"
              >
                <LifeBuoy className="h-5 w-5 mr-1.5" />
                How we help at claim time
                <ArrowRight className="h-5 w-5 ml-1.5" />
              </Button>
            </motion.div>
          </motion.div>

          {/* Advisor Card */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.35, duration: 0.6 }}
            className="flex justify-center lg:justify-end"
            data-testid="hero-advisor-card"
          >
            <div className="w-[220px] sm:w-[250px]">
              <div className="relative rounded-2xl overflow-hidden border-2 border-[#D4A017]/25 shadow-2xl shadow-black/40">
                <img 
                  src="https://customer-assets-cm19k8pv.emergentagent.net/job_1bbf805d-c799-41a3-98b1-da09b291e67c/artifacts/lwtz8zj6_hf_20260920_152704_24a85b0e-916a-46ee-86d2-90997ebecdba%20%281%29.png"
                  alt="Prashant K Uunadkat"
                  className="w-full aspect-[4/5] object-cover object-top"
                />
                <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-[#FFFFFF] via-[#F7F9FC]/70 to-transparent pt-12 pb-3 px-4">
                  <p className="text-[#1B2A4A] font-bold text-base leading-tight">Prashant K Uunadkat</p>
                  <p className="text-[#2E3B57] text-xs mt-0.5">Co-founder, Shree Finserv</p>
                </div>
              </div>
              <div className="mt-3 flex items-center justify-center gap-2 bg-[#EDF1F8]/50 border border-[#D4A017]/20 rounded-xl px-3 py-2">
                <BadgeCheck className="h-4 w-4 text-[#A87608] flex-shrink-0" />
                <p className="text-xs text-[#2E3B57]"><span className="text-[#1B2A4A] font-semibold">Beshak Verified</span> Insurance Advisor</p>
              </div>
              <a
                href="https://www.beshak.org/advisor/shree-finserv/"
                target="_blank"
                rel="noopener noreferrer"
                data-testid="beshak-verify-btn"
                className="mt-3 w-full inline-flex items-center justify-center gap-2 rounded-xl px-4 py-2.5 bg-[#A87608] hover:bg-[#8f6306] text-white text-sm font-semibold shadow-md shadow-[#A87608]/25 transition-colors"
              >
                <BadgeCheck className="h-4 w-4" /> Verify me on Beshak
                <ExternalLink className="h-4 w-4" />
              </a>
              <p className="mt-2 text-center text-[11px] text-[#3A4557]">
                8+ yrs expertise • CA (Inter) • 1000+ families guided
              </p>
            </div>
          </motion.div>
          </div>
        </div>
      </section>

      {/* Platform Sections - Main Navigation */}
      <section className="py-8 px-4 sm:px-6">
        <div className="max-w-7xl mx-auto">
          {/* Sections Grid */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-5"
          >
            {/* Explore All Plans */}
            <motion.div
              whileHover={{ y: -8, scale: 1.02 }}
              onClick={() => navigate("/explore-plans")}
              className="glass-card p-6 cursor-pointer group relative overflow-hidden"
              data-testid="explore-plans-section"
            >
              <div className="absolute top-0 right-0 w-32 h-32 bg-gradient-to-br from-[#D4A017]/15 to-[#E7EDF6]/20 rounded-full blur-2xl -translate-y-1/2 translate-x-1/2" />
              <div className="relative">
                <div className="h-14 w-14 rounded-2xl bg-gradient-to-br from-[#D4A017] to-[#D9A82C] flex items-center justify-center mb-4 group-hover:scale-110 transition-transform shadow-lg shadow-[#D4A017]/15">
                  <Layers className="h-7 w-7 text-[#0D1A3A]" />
                </div>
                <h3 className="font-bold text-[#1B2A4A] text-xl mb-2 group-hover:text-[#A87608] transition-colors">
                  Explore All Plans
                </h3>
                <p className="text-[#2E3B57] text-sm mb-4">
                  Health & Term insurance plans from top insurers
                </p>
                <div className="flex items-center gap-2">
                  <span className="text-xs px-2 py-1 rounded-full bg-[#D4A017]/15 text-[#A87608]">15 Health</span>
                  <span className="text-xs px-2 py-1 rounded-full bg-[#EDF1F8]/60 text-[#2E3B57]">2 Term</span>
                </div>
                <div className="flex items-center text-[#A87608] text-sm font-medium mt-4 group-hover:translate-x-2 transition-transform">
                  Browse Plans
                  <ArrowRight className="h-4 w-4 ml-1" />
                </div>
              </div>
            </motion.div>

            {/* Know Your Rights */}
            <motion.div
              whileHover={{ y: -8, scale: 1.02 }}
              onClick={() => navigate("/policyholder-rights")}
              className="glass-card p-6 cursor-pointer group relative overflow-hidden"
              data-testid="know-your-rights-section"
            >
              <div className="absolute top-0 right-0 w-32 h-32 bg-gradient-to-br from-[#D4A017]/15 to-[#D9A82C]/10 rounded-full blur-2xl -translate-y-1/2 translate-x-1/2" />
              <div className="relative">
                <div className="h-14 w-14 rounded-2xl bg-gradient-to-br from-[#D4A017] to-[#D9A82C] flex items-center justify-center mb-4 group-hover:scale-110 transition-transform shadow-lg shadow-[#D4A017]/15">
                  <Scale className="h-7 w-7 text-[#1B2A4A]" />
                </div>
                <h3 className="font-bold text-[#1B2A4A] text-xl mb-2 group-hover:text-[#A87608] transition-colors">
                  Know Your Rights
                </h3>
                <p className="text-[#3A4557] text-sm mb-4">
                  Claim process, grievance redressal, ombudsman & more
                </p>
                <div className="flex items-center gap-2">
                  <span className="text-xs px-2 py-1 rounded-full bg-[#D4A017]/15 text-[#A87608]">IRDAI Guidelines</span>
                </div>
                <div className="flex items-center text-[#A87608] text-sm font-medium mt-4 group-hover:translate-x-2 transition-transform">
                  Learn More
                  <ArrowRight className="h-4 w-4 ml-1" />
                </div>
              </div>
            </motion.div>

            {/* Claim Desk */}
            <motion.div
              whileHover={{ y: -8, scale: 1.02 }}
              onClick={() => navigate("/claim-desk")}
              className="glass-card p-6 cursor-pointer group relative overflow-hidden border-[#D4A017]/40"
              data-testid="claim-desk-section"
            >
              <div className="absolute top-0 right-0 w-32 h-32 bg-gradient-to-br from-[#D4A017]/20 to-emerald-500/10 rounded-full blur-2xl -translate-y-1/2 translate-x-1/2" />
              <div className="relative">
                <div className="h-14 w-14 rounded-2xl bg-gradient-to-br from-[#D4A017] to-[#D9A82C] flex items-center justify-center mb-4 group-hover:scale-110 transition-transform shadow-lg shadow-[#D4A017]/15">
                  <LifeBuoy className="h-7 w-7 text-[#0D1A3A]" />
                </div>
                <h3 className="font-bold text-[#1B2A4A] text-xl mb-2 group-hover:text-[#A87608] transition-colors">
                  Claim Desk
                </h3>
                <p className="text-[#2E3B57] text-sm mb-4">
                  24/7 claim assistance & dispute escalation support
                </p>
                <div className="flex items-center gap-2">
                  <span className="text-xs px-2 py-1 rounded-full bg-[#D4A017]/15 text-[#A87608]">24×7</span>
                  <span className="text-xs px-2 py-1 rounded-full bg-emerald-500/15 text-emerald-700">We escalate</span>
                </div>
                <div className="flex items-center text-[#A87608] text-sm font-medium mt-4 group-hover:translate-x-2 transition-transform">
                  How we help at claim time
                  <ArrowRight className="h-4 w-4 ml-1" />
                </div>
              </div>
            </motion.div>

            {/* Compare Plans */}
            <motion.div
              whileHover={{ y: -8, scale: 1.02 }}
              onClick={() => navigate("/compare")}
              className="glass-card p-6 cursor-pointer group relative overflow-hidden"
              data-testid="compare-plans-section"
            >
              <div className="absolute top-0 right-0 w-32 h-32 bg-gradient-to-br from-[#EDF1F8]/30 to-[#B8C3DA]/10 rounded-full blur-2xl -translate-y-1/2 translate-x-1/2" />
              <div className="relative">
                <div className="h-14 w-14 rounded-2xl bg-gradient-to-br from-[#EDF1F8] to-[#DCE4F2] flex items-center justify-center mb-4 group-hover:scale-110 transition-transform shadow-lg shadow-[#C9D4E5]/30 border border-[#B8C3DA]/20">
                  <BarChart3 className="h-7 w-7 text-[#2E3B57]" />
                </div>
                <h3 className="font-bold text-[#1B2A4A] text-xl mb-2 group-hover:text-[#2E3B57] transition-colors">
                  Compare Plans
                </h3>
                <p className="text-[#3A4557] text-sm mb-4">
                  Side-by-side comparison of up to 4 plans
                </p>
                <div className="flex items-center gap-2">
                  <span className="text-xs px-2 py-1 rounded-full bg-[#EDF1F8]/60 text-[#2E3B57]">PDF Export</span>
                </div>
                <div className="flex items-center text-[#A87608] text-sm font-medium mt-4 group-hover:translate-x-2 transition-transform">
                  Start Comparing
                  <ArrowRight className="h-4 w-4 ml-1" />
                </div>
              </div>
            </motion.div>

            {/* My Policy X-Ray - NEW */}
            <motion.div
              whileHover={{ y: -8, scale: 1.02 }}
              onClick={() => navigate("/policy-xray")}
              className="glass-card p-6 cursor-pointer group relative overflow-hidden border-purple-500/30"
              data-testid="policy-xray-section"
            >
              <div className="absolute top-2 right-2 px-2 py-0.5 rounded-full bg-gradient-to-r from-purple-500 to-pink-500 text-white text-xs font-bold animate-pulse">
                NEW
              </div>
              <div className="absolute top-0 right-0 w-32 h-32 bg-gradient-to-br from-purple-500/20 to-pink-500/20 rounded-full blur-2xl -translate-y-1/2 translate-x-1/2" />
              <div className="relative">
                <div className="h-14 w-14 rounded-2xl bg-gradient-to-br from-purple-500 to-pink-500 flex items-center justify-center mb-4 group-hover:scale-110 transition-transform shadow-lg shadow-purple-500/20">
                  <FileSearch className="h-7 w-7 text-[#1B2A4A]" />
                </div>
                <h3 className="font-bold text-[#1B2A4A] text-xl mb-2 group-hover:text-purple-600 transition-colors">
                  My Policy X-Ray
                </h3>
                <p className="text-[#3A4557] text-sm mb-4">
                  Upload your policy for AI-powered deep analysis
                </p>
                <div className="flex items-center gap-2">
                  <span className="text-xs px-2 py-1 rounded-full bg-purple-500/20 text-purple-600">AI-Powered</span>
                </div>
                <div className="flex items-center text-purple-600 text-sm font-medium mt-4 group-hover:translate-x-2 transition-transform">
                  Analyze Now
                  <ArrowRight className="h-4 w-4 ml-1" />
                </div>
              </div>
            </motion.div>

            {/* Claim Rejected? We'll Help — for people who bought elsewhere */}
            <motion.div
              whileHover={{ y: -8, scale: 1.02 }}
              onClick={() => navigate('/claim-help')}
              className="glass-card p-6 cursor-pointer group relative overflow-hidden border-[#D4A017]/30"
              data-testid="claim-help-section"
            >
              <div className="absolute top-0 right-0 w-32 h-32 bg-gradient-to-br from-rose-500/20 to-orange-500/20 rounded-full blur-2xl -translate-y-1/2 translate-x-1/2" />
              <div className="relative">
                <div className="h-14 w-14 rounded-2xl bg-gradient-to-br from-rose-500 to-orange-500 flex items-center justify-center mb-4 group-hover:scale-110 transition-transform shadow-lg shadow-rose-500/15">
                  <ShieldAlert className="h-7 w-7 text-white" />
                </div>
                <h3 className="font-bold text-[#1B2A4A] text-xl mb-2 group-hover:text-rose-600 transition-colors">
                  Claim Rejected? We'll Help
                </h3>
                <p className="text-[#3A4557] text-sm mb-4">
                  Bought your policy online and have no advisor? We'll review your case
                </p>
                <div className="flex items-center gap-2">
                  <span className="text-xs px-2 py-1 rounded-full bg-rose-500/15 text-rose-600">Free first review</span>
                  <span className="text-xs px-2 py-1 rounded-full bg-rose-500/15 text-rose-600">Any insurer</span>
                </div>
                <div className="flex items-center text-rose-600 text-sm font-medium mt-4 group-hover:translate-x-2 transition-transform">
                  Get Help Now
                  <ArrowRight className="h-4 w-4 ml-1" />
                </div>
              </div>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* Stats Band */}
      <section className="py-12 px-4 sm:px-6" data-testid="stats-band">
        <div className="max-w-6xl mx-auto grid grid-cols-2 lg:grid-cols-4 gap-4">
          <StatCounter end={1000} label="Families Protected" />
          <StatCounter end={8} label="Years of Expertise" />
          <StatCounter end={10} label="Insurers Compared" />
          <div className="glass-card p-6 text-center">
            <p className="text-3xl sm:text-4xl font-extrabold text-[#A87608]">24×7</p>
            <p className="text-[#2E3B57] text-sm mt-1">Claim Support</p>
          </div>
        </div>
      </section>

      {/* Insurer Logos Marquee */}
      <section className="py-10" data-testid="insurer-marquee">
        <p className="text-center text-[#3A4557] text-xs sm:text-sm uppercase tracking-[0.25em] mb-7 px-4">
          Advising across leading insurers
        </p>
        <div className="marquee-row">
          <div className="marquee-track">
            {[...INSURER_LOGOS, ...INSURER_LOGOS].map((logo, i) => (
              <div key={i} className="flex-shrink-0 h-14 w-40 bg-white rounded-xl flex items-center justify-center px-4 mx-3 shadow-lg shadow-black/20">
                <img
                  src={logo.url}
                  alt={logo.name}
                  className="max-h-9 max-w-[130px] object-contain"
                  loading="lazy"
                  onError={(e) => { e.currentTarget.parentElement.style.display = "none"; }}
                />
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Insurance Checklists - Compact Buttons */}
      <section className="py-12 px-4 sm:px-6">
        <div className="max-w-4xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-8"
          >
            <h2 className="text-2xl sm:text-3xl font-bold text-[#1B2A4A] mb-3">
              Essential Insurance Checklists
            </h2>
            <p className="text-[#3A4557]">
              Know what to look for before buying any policy
            </p>
          </motion.div>

          <div className="grid sm:grid-cols-2 gap-4">
            {/* Health Insurance Checklist Button */}
            <motion.button
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              onClick={() => setShowHealthModal(true)}
              className="glass-card p-6 text-left hover:border-blue-500/50 transition-all group"
            >
              <div className="flex items-center gap-4 mb-3">
                <div className="h-14 w-14 rounded-xl bg-gradient-to-br from-[#EDF1F8] to-[#DCE4F2] flex items-center justify-center group-hover:scale-110 transition-transform">
                  <HeartPulse className="h-7 w-7 text-[#1B2A4A]" />
                </div>
                <div>
                  <h3 className="font-bold text-[#1B2A4A] text-lg">Health Insurance</h3>
                  <p className="text-[#3A4557] text-sm">11 Key Points</p>
                </div>
              </div>
              <p className="text-[#3A4557] text-sm mb-3">
                Room rent, sub-limits, consumables, restore benefit & more
              </p>
              <div className="flex items-center text-[#A87608] text-sm font-medium">
                View Checklist
                <ChevronRight className="h-4 w-4 ml-1 group-hover:translate-x-1 transition-transform" />
              </div>
            </motion.button>

            {/* Term Insurance Checklist Button */}
            <motion.button
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.1 }}
              onClick={() => setShowTermModal(true)}
              className="glass-card p-6 text-left hover:border-[#D4A017]/50 transition-all group"
            >
              <div className="flex items-center gap-4 mb-3">
                <div className="h-14 w-14 rounded-xl bg-gradient-to-br from-[#D4A017] to-[#D9A82C] flex items-center justify-center group-hover:scale-110 transition-transform">
                  <Shield className="h-7 w-7 text-[#1B2A4A]" />
                </div>
                <div>
                  <h3 className="font-bold text-[#1B2A4A] text-lg">Term Insurance</h3>
                  <p className="text-[#3A4557] text-sm">8 Key Points</p>
                </div>
              </div>
              <p className="text-[#3A4557] text-sm mb-3">
                Cover amount, claim ratio, riders, premium & more
              </p>
              <div className="flex items-center text-[#A87608] text-sm font-medium">
                View Checklist
                <ChevronRight className="h-4 w-4 ml-1 group-hover:translate-x-1 transition-transform" />
              </div>
            </motion.button>
          </div>
        </div>
      </section>

      {/* Health Insurance Checklist Modal */}
      {showHealthModal && (
        <div className="fixed inset-0 z-50 flex items-start justify-center p-4 pt-8 overflow-y-auto">
          <div className="fixed inset-0 bg-black/80 backdrop-blur-sm" onClick={() => setShowHealthModal(false)} />
          <motion.div
            initial={{ opacity: 0, scale: 0.95, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            className="relative w-full max-w-3xl glass-card p-4 sm:p-6 my-4 max-h-[90vh] overflow-y-auto"
          >
            <div className="flex items-center justify-between mb-6 sticky top-0 bg-white py-2 -mt-2 z-10">
              <div className="flex items-center gap-3">
                <div className="h-10 w-10 rounded-xl bg-gradient-to-br from-[#EDF1F8] to-[#DCE4F2] flex items-center justify-center">
                  <HeartPulse className="h-5 w-5 text-[#1B2A4A]" />
                </div>
                <h2 className="font-bold text-[#1B2A4A] text-xl">Health Insurance Checklist</h2>
              </div>
              <button 
                onClick={() => setShowHealthModal(false)}
                className="p-2 hover:bg-[#EDF1F8]/40 rounded-lg text-[#3A4557] hover:text-[#1B2A4A] transition-colors"
              >
                <XCircle className="h-6 w-6" />
              </button>
            </div>
            
            <div className="space-y-3">
              {HEALTH_CHECKLIST.map((item, idx) => {
                const isExpanded = expandedChecklist[idx];
                return (
                  <div key={idx} className="border border-[#C9D4E6] rounded-xl overflow-hidden">
                    <button
                      onClick={() => toggleChecklistItem(idx)}
                      className="w-full flex items-center gap-3 p-4 hover:bg-[#EDF1F8]/20 transition-colors text-left"
                    >
                      <div className="h-10 w-10 rounded-lg bg-[#EDF1F8]/40 flex items-center justify-center flex-shrink-0">
                        <item.icon className="h-5 w-5 text-[#2E3B57]" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <span className="text-[#A87608] font-bold text-sm">#{idx + 1}</span>
                          <h3 className="font-semibold text-[#1B2A4A] text-sm sm:text-base">{item.title}</h3>
                        </div>
                        <p className="text-[#3A4557] text-xs sm:text-sm">{item.shortDesc}</p>
                      </div>
                      <ChevronRight className={`h-5 w-5 text-[#3A4557] transition-transform flex-shrink-0 ${isExpanded ? 'rotate-90' : ''}`} />
                    </button>
                    {isExpanded && (
                      <div className="border-t border-[#C9D4E6] p-4 space-y-3 bg-white/40">
                        <p className="text-[#2E3B57] text-sm">{item.fullDesc}</p>
                        <div className="bg-[#EDF1F8]/30 rounded-lg p-3 border border-[#C9D4E6]">
                          <p className="text-[#2E3B57] text-xs sm:text-sm">{item.example}</p>
                        </div>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                          <div className="bg-emerald-500/10 rounded-lg p-3 border border-emerald-500/20">
                            <p className="font-semibold text-emerald-600 text-xs mb-1">✓ Look For</p>
                            <p className="text-[#2E3B57] text-xs">{item.lookFor}</p>
                          </div>
                          <div className="bg-red-500/10 rounded-lg p-3 border border-red-500/20">
                            <p className="font-semibold text-red-600 text-xs mb-1">✗ Avoid</p>
                            <p className="text-[#2E3B57] text-xs">{item.avoid}</p>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
            
            <div className="mt-6 pt-4 border-t border-[#C9D4E6] flex flex-col sm:flex-row gap-3">
              <Button onClick={() => { setShowHealthModal(false); navigate("/compare"); }} className="btn-gold flex-1">
                <Search className="mr-2 h-4 w-4" />
                Compare Health Plans
              </Button>
              <Button onClick={() => setShowHealthModal(false)} variant="outline" className="btn-outline flex-1">
                Close
              </Button>
            </div>
          </motion.div>
        </div>
      )}

      {/* Term Insurance Checklist Modal */}
      {showTermModal && (
        <div className="fixed inset-0 z-50 flex items-start justify-center p-4 pt-8 overflow-y-auto">
          <div className="fixed inset-0 bg-black/80 backdrop-blur-sm" onClick={() => setShowTermModal(false)} />
          <motion.div
            initial={{ opacity: 0, scale: 0.95, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            className="relative w-full max-w-3xl glass-card p-4 sm:p-6 my-4 max-h-[90vh] overflow-y-auto"
          >
            <div className="flex items-center justify-between mb-6 sticky top-0 bg-white py-2 -mt-2 z-10">
              <div className="flex items-center gap-3">
                <div className="h-10 w-10 rounded-xl bg-gradient-to-br from-[#D4A017] to-[#D9A82C] flex items-center justify-center">
                  <Shield className="h-5 w-5 text-[#1B2A4A]" />
                </div>
                <h2 className="font-bold text-[#1B2A4A] text-xl">Term Insurance Checklist</h2>
              </div>
              <button 
                onClick={() => setShowTermModal(false)}
                className="p-2 hover:bg-[#D4A017]/15 rounded-lg text-[#3A4557] hover:text-[#1B2A4A] transition-colors"
              >
                <XCircle className="h-6 w-6" />
              </button>
            </div>
            
            <div className="space-y-3">
              {TERM_CHECKLIST.map((item, idx) => {
                const isExpanded = expandedTermChecklist[idx];
                return (
                  <div key={idx} className="border border-[#D4A017]/20 rounded-xl overflow-hidden">
                    <button
                      onClick={() => toggleTermChecklistItem(idx)}
                      className="w-full flex items-center gap-3 p-4 hover:bg-[#D4A017]/10 transition-colors text-left"
                    >
                      <div className="h-10 w-10 rounded-lg bg-[#D4A017]/15 flex items-center justify-center flex-shrink-0">
                        <item.icon className="h-5 w-5 text-[#A87608]" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <span className="text-[#A87608] font-bold text-sm">#{idx + 1}</span>
                          <h3 className="font-semibold text-[#1B2A4A] text-sm sm:text-base">{item.title}</h3>
                        </div>
                        <p className="text-[#3A4557] text-xs sm:text-sm">{item.shortDesc}</p>
                      </div>
                      <ChevronRight className={`h-5 w-5 text-[#3A4557] transition-transform flex-shrink-0 ${isExpanded ? 'rotate-90' : ''}`} />
                    </button>
                    {isExpanded && (
                      <div className="border-t border-[#D4A017]/20 p-4 space-y-3 bg-[#D4A017]/8">
                        <p className="text-[#2E3B57] text-sm">{item.fullDesc}</p>
                        <div className="bg-[#D4A017]/10 rounded-lg p-3 border border-[#D4A017]/20">
                          <p className="text-[#8F6A06] text-xs sm:text-sm">{item.example}</p>
                        </div>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                          <div className="bg-emerald-500/10 rounded-lg p-3 border border-emerald-500/20">
                            <p className="font-semibold text-emerald-600 text-xs mb-1">✓ Look For</p>
                            <p className="text-[#2E3B57] text-xs">{item.lookFor}</p>
                          </div>
                          <div className="bg-red-500/10 rounded-lg p-3 border border-red-500/20">
                            <p className="font-semibold text-red-600 text-xs mb-1">✗ Avoid</p>
                            <p className="text-[#2E3B57] text-xs">{item.avoid}</p>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
            
            <div className="mt-6 pt-4 border-t border-[#D4A017]/20 flex flex-col sm:flex-row gap-3">
              <a href="tel:+919510020907" className="flex-1">
                <Button className="btn-gold w-full">
                  <Phone className="mr-2 h-4 w-4" />
                  Get Free Consultation
                </Button>
              </a>
              <Button onClick={() => setShowTermModal(false)} variant="outline" className="btn-outline flex-1">
                Close
              </Button>
            </div>
          </motion.div>
        </div>
      )}

      {/* How It Works */}
      <section className="py-20 px-4 sm:px-6">
        <div className="max-w-7xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <span className="badge-gold mb-4 inline-flex">
              <Zap className="h-4 w-4" />
              How It Works
            </span>
            <h2 className="text-3xl sm:text-4xl font-bold text-[#1B2A4A] mb-4">
              Get the Right Plan in 3 Steps
            </h2>
          </motion.div>

          <div className="grid md:grid-cols-3 gap-6">
            {[
              { num: "01", title: "Explore & Compare", desc: "Browse plans from top insurers and compare features side-by-side", icon: Search, color: "blue" },
              { num: "02", title: "Understand Benefits", desc: "Read detailed explanations of coverage, waiting periods, and riders", icon: FileText, color: "blue" },
              { num: "03", title: "Get Expert Advice", desc: "Call us for personalized recommendations based on your needs", icon: Phone, color: "gold" }
            ].map((step, idx) => (
              <motion.div
                key={idx}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: idx * 0.2 }}
                className="feature-box relative"
              >
                <div className={`absolute -top-3 -right-3 h-12 w-12 rounded-xl bg-gradient-to-br ${
                  step.color === 'blue' ? 'from-[#EDF1F8] to-[#DCE4F2]' :
                  'from-[#D4A017] to-[#D9A82C]'
                } flex items-center justify-center text-[#1B2A4A] font-bold`}>
                  {step.num}
                </div>
                <step.icon className={`h-10 w-10 mb-4 ${
                  step.color === 'blue' ? 'text-[#2E3B57]' : 'text-[#A87608]'
                }`} />
                <h3 className="text-xl font-bold text-[#1B2A4A] mb-2">{step.title}</h3>
                <p className="text-[#3A4557]">{step.desc}</p>
              </motion.div>
            ))}
          </div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mt-10"
          >
            <a href="tel:+919510020907">
              <Button className="btn-blue text-lg h-14 px-8">
                <Phone className="mr-2 h-5 w-5" />
                Talk to an Expert
              </Button>
            </a>
          </motion.div>
        </div>
      </section>

      {/* Google Reviews - Auto-scrolling */}
      {reviewsData.reviews.length > 0 && (
        <section className="py-20 section-blue" data-testid="google-reviews-section">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 text-center mb-10">
            <span className="badge-gold mb-4 inline-flex">
              <Star className="h-4 w-4" />
              What Clients Say
            </span>
            <h2 className="text-3xl sm:text-4xl font-bold text-[#1B2A4A] mb-3 flex items-center justify-center gap-3 flex-wrap">
              Rated {reviewsData.settings.google_rating || 5}
              <Star className="h-7 w-7 text-[#A87608] fill-[#D4A017] inline" />
              on Google
            </h2>
            {googleUrl ? (
              <a
                href={googleUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="text-[#A87608] text-sm font-medium hover:underline inline-flex items-center gap-1"
                data-testid="read-all-reviews-link"
              >
                Read all reviews on Google
                <ArrowRight className="h-4 w-4" />
              </a>
            ) : (
              <p className="text-[#3A4557] text-sm">Real experiences from families we've protected</p>
            )}
          </div>
          <div className="marquee-row" data-testid="reviews-marquee">
            <div className="marquee-track marquee-slow items-stretch">
              {[...reviewsData.reviews, ...reviewsData.reviews].map((review, i) => (
                <ReviewCard key={`${review.id}-${i}`} review={review} />
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Client Testimonials - approved video & written testimonials */}
      {testimonials.length > 0 && (
        <section className="py-20 px-4 sm:px-6" data-testid="client-testimonials-section">
          <div className="max-w-7xl mx-auto text-center mb-10">
            <span className="badge-gold mb-4 inline-flex">
              <Quote className="h-4 w-4" />
              In Their Own Words
            </span>
            <h2 className="text-3xl sm:text-4xl font-bold text-[#1B2A4A] mb-3">Client Testimonials</h2>
            <p className="text-[#3A4557] text-sm">Real stories from families we've helped protect</p>
          </div>
          <div className="flex flex-wrap justify-center gap-y-6" data-testid="testimonials-grid">
            {testimonials.map((item) => (
              <TestimonialCard key={item.id} item={item} />
            ))}
          </div>
        </section>
      )}

      {/* FAQ */}
      <section className="py-16 px-4 sm:px-6" data-testid="faq-section">
        <div className="max-w-3xl mx-auto">
          <div className="text-center mb-10">
            <span className="badge-gold mb-4 inline-flex">
              <FileText className="h-4 w-4" />
              Questions
            </span>
            <h2 className="text-3xl sm:text-4xl font-bold text-[#1B2A4A]">
              Everything people ask us first
            </h2>
          </div>
          <div className="space-y-3">
            {LANDING_FAQS.map((faq, idx) => (
              <div key={idx} className="glass-card overflow-hidden">
                <button
                  onClick={() => setOpenFaq(openFaq === idx ? null : idx)}
                  className="w-full flex items-center justify-between gap-4 p-5 text-left"
                  data-testid={`faq-toggle-${idx}`}
                >
                  <span className="text-[#1B2A4A] font-semibold text-sm sm:text-base">{faq.q}</span>
                  <ChevronDown className={`h-5 w-5 text-[#A87608] flex-shrink-0 transition-transform ${openFaq === idx ? "rotate-180" : ""}`} />
                </button>
                {openFaq === idx && (
                  <div className="px-5 pb-5 -mt-1">
                    <p className="text-[#2E3B57] text-sm leading-relaxed">{faq.a}</p>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 px-4 sm:px-6">
        <div className="max-w-4xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="glass-card p-10 md:p-16 text-center relative overflow-hidden"
          >
            <div className="absolute top-0 right-0 w-64 h-64 bg-[#D4A017]/10 rounded-full blur-3xl" />
            <div className="absolute bottom-0 left-0 w-64 h-64 bg-[#EDF1F8]/40 rounded-full blur-3xl" />
            
            <div className="relative z-10">
              <h2 className="text-2xl sm:text-3xl font-bold text-[#1B2A4A] mb-4">
                Not Sure Which Plan to Choose?
              </h2>
              <p className="text-[#2E3B57] mb-8 max-w-xl mx-auto">
                Get free, unbiased advice from an experienced insurance advisor. No sales pressure, just honest guidance.
              </p>
              <a href="tel:+919510020907">
                <Button className="btn-gold text-lg h-14 px-8">
                  <Phone className="mr-2 h-5 w-5" />
                  Call Now - Free Consultation
                </Button>
              </a>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-10 px-4 sm:px-6 border-t border-[#C9D4E6]">
        <div className="max-w-7xl mx-auto flex flex-col md:flex-row items-center justify-between gap-6">
          <div className="flex items-center gap-4">
            <img src="/kavachbox-logo-light.png" alt="Kavach Box" className="h-12 object-contain" />
            <div className="border-l border-[#C9D4E6] pl-4">
              <div className="flex items-center gap-2">
                <img src="/shree-finserv-logo.png" alt="Shree Finserv" className="h-8 object-contain" />
              </div>
              <p className="text-xs text-[#2E3B57] mt-1">A part of <span className="font-semibold">Shree Finserv Group</span></p>
              <p className="text-xs text-[#3A4557]">by Prashant K Uunadkat</p>
            </div>
          </div>
          
          <div className="flex items-center gap-6">
            <a href="tel:+919510020907" className="text-[#3A4557] hover:text-[#A87608] transition-colors">
              <Phone className="h-5 w-5" />
            </a>
            <a href="https://wa.me/919510020907" target="_blank" rel="noopener noreferrer" className="text-[#3A4557] hover:text-[#A87608] transition-colors">
              <Heart className="h-5 w-5" />
            </a>
            <a href="mailto:prasant@shreefinservices.com" className="text-[#3A4557] hover:text-[#A87608] transition-colors">
              <Mail className="h-5 w-5" />
            </a>
          </div>
          
          <div className="flex items-center gap-4">
            <button 
              onClick={() => navigate('/policyholder-rights')}
              className="text-sm text-[#3A4557] hover:text-[#A87608] transition-colors flex items-center gap-1"
              data-testid="policyholder-rights-link"
            >
              <Shield className="h-4 w-4" />
              Know Your Rights
            </button>
            <span className="text-[#15294D]">|</span>
            <button 
              onClick={() => navigate('/client/login')}
              className="text-sm text-[#3A4557] hover:text-[#A87608] transition-colors"
              data-testid="client-login-link"
            >
              Client Login
            </button>
            <span className="text-[#15294D]">|</span>
            <button 
              onClick={() => navigate('/advisor/login')}
              className="text-sm text-[#3A4557] hover:text-[#A87608] transition-colors"
              data-testid="admin-login-link"
            >
              Admin Login
            </button>
            <span className="text-[#15294D]">|</span>
            <p className="text-sm text-[#3A4557]">
              © 2025 Shree Finserv
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
}
